import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useNavigationType, useLocation } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import api from '../../utils/api';
import ProductCard from '../../components/common/ProductCard';
import FilterBar from '../../components/common/FilterBar';
import toast from 'react-hot-toast';
import { getPageCache, setPageCache } from '../../utils/pageCache';
import { forceTopOnReturn } from '../../utils/scrollMemory';

function SkeletonCard() {
  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 animate-pulse">
      <div className="aspect-square bg-gray-100" />
      <div className="p-3 space-y-2">
        <div className="h-3 bg-gray-100 rounded w-1/2" />
        <div className="h-4 bg-gray-100 rounded w-3/4" />
        <div className="h-4 bg-gray-100 rounded w-1/2" />
        <div className="h-5 bg-gray-100 rounded w-1/3" />
      </div>
    </div>
  );
}

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navType = useNavigationType();
  const location = useLocation();
  // Hydrate from cache on Back/Forward so the same list paints instantly and
  // scroll restoration can land on the clicked card without a refetch flash.
  const cacheKey = `products?${searchParams.toString()}`;
  const cachedList = getPageCache(cacheKey);
  const [products, setProducts] = useState(cachedList?.products || []);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(!cachedList);
  const [total, setTotal] = useState(cachedList?.total || 0);
  const [totalPages, setTotalPages] = useState(cachedList?.totalPages || 1);
  const [priceMin, setPriceMin] = useState(() => parseInt(new URLSearchParams(window.location.search).get('priceMin') || '0'));
  const [priceMax, setPriceMax] = useState(() => parseInt(new URLSearchParams(window.location.search).get('priceMax') || '99999'));
  const [localSearch, setLocalSearch] = useState('');

  const page = parseInt(searchParams.get('page') || '1');
  const search = searchParams.get('search') || '';
  const selectedCategory = searchParams.get('category') || '';
  const sortValue = searchParams.get('sort') || 'createdAt-DESC';
  const idsParam = searchParams.get('ids') || ''; // curated set from a homepage "View All"

  // Sync localSearch with URL param
  useEffect(() => { setLocalSearch(search); }, [search]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    next.delete('page');
    setSearchParams(next);
  };

  const gotoPage = (p) => {
    // Land on the header (not the saved deep-scroll) when this entry is returned
    // to via Back — we only scrolled down to reach the pagination controls.
    forceTopOnReturn(location.key);
    const next = new URLSearchParams(searchParams);
    next.set('page', p);
    setSearchParams(next);
    window.scrollTo(0, 0);
  };

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const [sort, order] = sortValue.split('-');
      const params = { page, limit: 20, sort, order };
      if (idsParam) params.ids = idsParam;
      if (search) params.search = search;
      if (selectedCategory) params.category = selectedCategory;
      if (priceMin > 0) params.priceMin = priceMin;
      if (priceMax < 99999) params.priceMax = priceMax;
      const { data } = await api.get('/products', { params });
      setProducts(data.products || []);
      setTotal(data.total || 0);
      setTotalPages(data.pages || 1);
      setPageCache(`products?${searchParams.toString()}`, {
        products: data.products || [], total: data.total || 0, totalPages: data.pages || 1,
      });
    } catch { toast.error('Failed to load products'); }
    finally { setLoading(false); }
  }, [page, search, selectedCategory, sortValue, priceMin, priceMax, idsParam, searchParams]);

  // Don't jump to the top when arriving via Back/Forward — let ScrollManager
  // restore the position the user clicked from. Only scroll up on real
  // navigations (new filter/sort/page).
  useEffect(() => {
    fetchProducts();
    if (navType !== 'POP') window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [fetchProducts, navType]);

  useEffect(() => {
    api.get('/categories').then(r => {
      const cats = r.data.categories || [];
      setCategories(cats);
      if (selectedCategory && isNaN(selectedCategory)) {
        const match = cats.find(c => c.slug === selectedCategory);
        if (match) setParam('category', match.id);
      }
    }).catch(() => {});
  }, []);

  const handleReset = () => {
    setSearchParams({});
    setLocalSearch('');
    setPriceMin(0);
    setPriceMax(99999);
  };

  const activeFilterCount = [selectedCategory, priceMin > 0 || priceMax < 99999].filter(Boolean).length;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 text-sm text-gray-400 mb-4">
        <Link to="/" className="hover:text-brand-yellow">Home</Link>
        <span>›</span>
        <span className="text-brand-dark font-medium">All Products</span>
        {selectedCategory && categories.find(c => c.id == selectedCategory) && (
          <>
            <span>›</span>
            <span className="text-brand-dark font-medium">{categories.find(c => c.id == selectedCategory)?.name}</span>
          </>
        )}
      </div>

      {/* Search bar */}
      <form onSubmit={e => { e.preventDefault(); setParam('search', localSearch); }}
        className="relative mb-4 max-w-xl">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={localSearch}
          onChange={e => setLocalSearch(e.target.value)}
          placeholder="Search products..."
          className="w-full pl-10 pr-10 py-2.5 text-sm border border-gray-200 rounded-full bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-yellow focus:bg-white transition-all"
        />
        {localSearch && (
          <button type="button" onClick={() => { setLocalSearch(''); setParam('search', ''); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-brand-dark">
            <X className="w-4 h-4" />
          </button>
        )}
      </form>

      {/* Horizontal filter bar */}
      <FilterBar
        sortValue={sortValue}
        onSortChange={v => setParam('sort', v)}
        priceMin={priceMin}
        priceMax={priceMax}
        onPriceChange={(min, max) => { setPriceMin(min); setPriceMax(max); }}
        categories={categories}
        selectedCategory={selectedCategory}
        onCategoryChange={v => setParam('category', v)}
        activeSearch={search}
        onClearSearch={() => { setParam('search', ''); setLocalSearch(''); }}
        total={total}
        loading={loading}
        activeFilterCount={activeFilterCount}
      />

      {/* Products grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
        {loading && products.length === 0
          ? Array.from({ length: 12 }).map((_, i) => <SkeletonCard key={i} />)
          : products.length === 0
            ? (
              <div className="col-span-full text-center py-20">
                <p className="text-5xl mb-4">😕</p>
                <p className="text-lg font-bold text-brand-dark mb-2">No products found</p>
                <p className="text-gray-400 text-sm mb-5">Try a different search or filter</p>
                <button onClick={handleReset} className="btn-primary rounded-full px-6 py-2.5 text-sm">Clear Filters</button>
              </div>
            )
            : products.map(p => <ProductCard key={p.id} product={p} />)
        }
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-8">
          <button disabled={page === 1}
            onClick={() => gotoPage(page - 1)}
            className="px-4 py-2 rounded-full border border-gray-200 text-sm font-medium disabled:opacity-40 hover:border-brand-dark transition-colors">
            ← Prev
          </button>
          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
            const p = Math.max(1, page - 2) + i;
            if (p > totalPages) return null;
            return (
              <button key={p}
                onClick={() => gotoPage(p)}
                className={`w-9 h-9 rounded-full text-sm font-medium transition-colors ${p === page ? 'bg-brand-yellow text-brand-dark' : 'border border-gray-200 text-gray-600 hover:border-brand-dark'}`}>
                {p}
              </button>
            );
          })}
          <button disabled={page === totalPages}
            onClick={() => gotoPage(page + 1)}
            className="px-4 py-2 rounded-full border border-gray-200 text-sm font-medium disabled:opacity-40 hover:border-brand-dark transition-colors">
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
